import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Reveal, RevealText } from "@project/user/components/motion";
import { useI18n } from "@project/lib/use-i18n";
import { cn } from "@project/lib/utils";
import { HERO_IMAGES, FALLBACK_GALLERY } from "@project/lib/stock-images";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@project/components/ui/carousel";

const INITIATIVE_SLIDES = [
  {
    key: "elderCare",
    to: "/elder-care-program",
    image: HERO_IMAGES.residentCare,
  },
  {
    key: "healthcare",
    to: "/healthcare",
    image: HERO_IMAGES.about,
  },
  {
    key: "gaushala",
    to: "/gaushala",
    image: FALLBACK_GALLERY[5],
  },
  {
    key: "donate",
    to: "/donate",
    image: HERO_IMAGES.donate,
  },
  {
    key: "tiffin",
    to: "/tiffin-seva",
    image: HERO_IMAGES.tiffin,
  },
  {
    key: "gallery",
    to: "/gallery",
    image: HERO_IMAGES.gallery,
  },
] as const;

export function AboutInitiativesCarousel() {
  const { t } = useI18n();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    onSelect();
    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  return (
    <section className="mx-auto max-w-site px-4 py-16 md:py-20">
      <Reveal variant="left" className="max-w-3xl text-left">
        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-gold">
          {t("pages.about.initiatives.eyebrow")}
        </p>
        <h2 className="mt-3 font-serif !text-[2.8rem] text-foreground">
          <RevealText delay={120}>
            {t("pages.about.initiatives.heading")}
          </RevealText>
        </h2>
        <p className="mt-3 text-muted-foreground">
          {t("pages.about.initiatives.description")}
        </p>
      </Reveal>

      <Reveal variant="morph" delay={160} className="mt-10">
        <Carousel
          setApi={setApi}
          opts={{ loop: true, align: "start" }}
          className="relative w-full"
        >
          <CarouselContent className="ml-0">
            {INITIATIVE_SLIDES.map(slide => (
              <CarouselItem key={slide.key} className="pl-0">
                <div className="group relative h-[210px] overflow-hidden rounded-2xl border border-border shadow-sm sm:h-[260px] md:h-[400px]">
                  <img
                    src={slide.image}
                    alt={t(`pages.about.initiatives.slides.${slide.key}.title`)}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 md:p-8">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">
                      {t(`pages.about.initiatives.slides.${slide.key}.label`)}
                    </p>
                    <h3 className="mt-1.5 font-serif text-xl !text-white sm:text-2xl md:text-3xl">
                      {t(`pages.about.initiatives.slides.${slide.key}.title`)}
                    </h3>
                    <p className="mt-1.5 line-clamp-2 max-w-xl text-sm leading-relaxed text-white/90">
                      {t(`pages.about.initiatives.slides.${slide.key}.body`)}
                    </p>
                    <Link
                      to={slide.to}
                      className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-white transition hover:text-gold"
                    >
                      {t(`pages.about.initiatives.slides.${slide.key}.cta`)}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>

          <CarouselPrevious className="left-3 top-1/2 z-10 h-9 w-9 -translate-y-1/2 border-0 bg-white text-primary shadow-md hover:bg-white disabled:opacity-40 md:left-4" />
          <CarouselNext className="right-3 top-1/2 z-10 h-9 w-9 -translate-y-1/2 border-0 bg-white text-primary shadow-md hover:bg-white disabled:opacity-40 md:right-4" />
        </Carousel>

        <div className="mt-5 flex items-center justify-center gap-2">
          {INITIATIVE_SLIDES.map((slide, index) => (
            <button
              key={slide.key}
              type="button"
              aria-label={`Go to slide ${index + 1}`}
              aria-current={current === index}
              onClick={() => api?.scrollTo(index)}
              className={cn(
                "rounded-full transition-all duration-300",
                current === index
                  ? "h-2 w-6 bg-primary"
                  : "h-2 w-2 bg-[#c8cdd5] hover:bg-[#9aa3af]"
              )}
            />
          ))}
        </div>
      </Reveal>
    </section>
  );
}
